import { match } from 'ts-pattern';

import type { LinePayload } from '~/api/common/payload.types';
import type { CalculationData, EntryLine, Line } from '~/api/common/types';

import { insertIf } from '~/utils/arrays';

import type { DocumentFormLine, DocumentFormSection } from './types';

export const groupLinesInFormSections = (lines: Line[]): DocumentFormSection[] => {
  if (lines.length === 0) {
    return [{ isPhotoAlbum: false, lines: [] }];
  }

  const photoAlbumSection = { isPhotoAlbum: true, lines: lines.filter((line) => line.isPhotoAlbum) };
  const defaultSection = { isPhotoAlbum: false, lines: lines.filter((line) => !line.isPhotoAlbum) };

  return [...insertIf(photoAlbumSection.lines.length > 0, photoAlbumSection), defaultSection].map((section) => ({
    ...section,
    lines: section.lines.map((line): DocumentFormLine =>
      line.type === 'entry'
        ? {
            uuid: line.uuid,
            type: 'entry',
            description: line.description,
            price: line.price,
            discountType: line.discountType,
            discountValue: line.discountValue,
            quantity: line.quantity,
            unit: line.unit,
            activityType: line.activityType,
            productId: line.productId,
          }
        : {
            uuid: line.uuid,
            type: 'text',
            description: line.description,
          },
    ),
  }));
};

export const flattenFormSectionsToLines = (sections: DocumentFormSection[]): LinePayload[] =>
  sections
    .flatMap((section) =>
      section.lines.map((line) =>
        line.type === 'entry'
          ? {
              uuid: line.uuid,
              type: 'entry' as const,
              isPhotoAlbum: section.isPhotoAlbum,
              description: line.description,
              price: line.price ?? 0,
              discountType: line.discountType,
              discountValue: line.discountValue,
              quantity: line.quantity ?? 0,
              unit: line.unit,
              activityType: line.activityType,
              productId: line.productId,
            }
          : {
              uuid: line.uuid,
              type: 'text' as const,
              isPhotoAlbum: section.isPhotoAlbum,
              description: line.description,
            },
      ),
    )
    .map((line, i) => ({ ...line, position: i + 1 }));

export const determineLineVatPercentage = (line: Line, calculationData: CalculationData): 0 | 0.06 | 0.21 => {
  if (calculationData.clientIsExemptFromVat) return 0;

  if (line.isPhotoAlbum) return 0.06;

  return 0.21;
};

export const calculateLineDiscount = (line: EntryLine): number => {
  const total = line.price * line.quantity;

  const discount = match(line.discountType)
    .with('flatRate', () => line.discountValue ?? 0)
    .with('percentage', () => ((line.discountValue ?? 0) / 100) * total)
    .otherwise(() => 0);

  return Math.round(discount * 100) / 100;
};
